/**
 * UI Module Types
 * Type definitions for UI components
 */

/**
 * UI Element types enum
 * Use UIManager.Type.Joystick instead of typing 'joystick'
 */
export enum UIElementType {
    Joystick = 'joystick',
    Button = 'button',
    HUD = 'hud'
}

/**
 * Joystick positioning mode
 */
export type JoystickPositionMode = 'fixed' | 'dynamic';

/**
 * Joystick visibility mode
 */
export type JoystickVisibilityMode = 'always' | 'on-touch';

/**
 * Joystick visual configuration
 */
export interface JoystickUIConfig {
    /** UI element type - use UIManager.Type.Joystick */
    type: 'joystick';

    /** Unique identifier for the joystick */
    id: string;

    /** Position on screen (for fixed mode) */
    position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' | 'custom';

    /** Custom position (0-1 normalized screen coordinates) */
    customPosition?: { x: number; y: number };

    /** Positioning mode: 'fixed' stays at position, 'dynamic' follows touch (default: 'dynamic') */
    positionMode?: JoystickPositionMode;

    /** Visibility mode: 'always' shows always, 'on-touch' appears on touch (default: 'on-touch') */
    visibilityMode?: JoystickVisibilityMode;

    /** Margin from edge for fixed positioning (should match Input_Module's zoneRadius, default: 80) */
    positionMargin?: number;

    /** Size of the joystick container in pixels */
    size?: number;

    /** Size of the inner knob in pixels */
    knobSize?: number;

    /** Colors */
    baseColor?: string;
    knobColor?: string;
    activeColor?: string;

    /** Opacity */
    opacity?: number;

    /** Z-index for layering */
    zIndex?: number;

    /** Whether to show on desktop (default: false, mobile only) */
    showOnDesktop?: boolean;

    /** Container element to append to (default: document.body) */
    container?: HTMLElement;
}

/**
 * Joystick UI state (read-only, for rendering)
 */
export interface JoystickUIState {
    id: string;
    isActive: boolean;
    knobPosition: { x: number; y: number }; // Relative to center (-1 to 1)
    element: HTMLElement;
    baseElement: HTMLElement;
    knobElement: HTMLElement;
}

/**
 * Button UI configuration
 */
export interface ButtonUIConfig {
    /** UI element type */
    type: 'button';

    id: string;
    label?: string;
    icon?: string;
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom';
    customPosition?: { x: number; y: number };
    size?: number;
    color?: string;
    zIndex?: number;
    showOnDesktop?: boolean;
    container?: HTMLElement;
}

/**
 * HUD element configuration
 */
export interface HUDConfig {
    /** UI element type */
    type: 'hud';

    id: string;
    hudType: 'text' | 'bar' | 'icon' | 'custom';
    position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'custom';
    customPosition?: { x: number; y: number };
    zIndex?: number;
    container?: HTMLElement;
}

/**
 * Discriminated union of all UI element configs
 */
export type UIElementConfig = JoystickUIConfig | ButtonUIConfig | HUDConfig;

/**
 * Button instance interface (placeholder for future)
 */
export interface IButton {
    readonly id: string;
    setVisible(visible: boolean): void;
    remove(): void;
    getElement(): HTMLElement;
}

/**
 * HUD instance interface (placeholder for future)
 */
export interface IHUD {
    readonly id: string;
    update(value: any): void;
    setVisible(visible: boolean): void;
    remove(): void;
    getElement(): HTMLElement;
}

/**
 * Joystick instance interface
 */
export interface IJoystick {
    readonly id: string;
    update(vector: { x: number; y: number }, isActive: boolean, centerPosition?: { x: number; y: number }): void;
    setVisible(visible: boolean): void;
    remove(): void;
    getElement(): HTMLElement;
}

/**
 * Discriminated union of all UI element instances
 */
export type UIElement = IJoystick | IButton | IHUD;

